/**
 * Motion system (ADR 081, EPT-176 → applied by EPT-184). The single source for
 * the overlay animation contract, so every overlay surface shares it verbatim
 * instead of hand-copying the same `data-[state=…]:animate-*` strings.
 *
 * Contract (asymmetric, refined-minimal §1.2):
 * - **Enter** at `--duration-base`, **exit** at `--duration-fast` — dismissal is
 *   always quicker than entry — both on the `--ease-out` curve. The
 *   `animate-in`/`animate-out` keyframes read `--tw-duration` (set by
 *   `duration-*`) and `--tw-ease` (set by `ease-out`), so those utilities drive
 *   the animation timing here, not just transitions.
 * - **Anchored** overlays (popover-style: dropdown, select, tooltip, and the
 *   future generic Popover) grow *from their trigger* via the shared Radix
 *   popper transform-origin plus a short directional slide toward the anchor.
 *   Centered overlays (Dialog) keep the default center origin.
 *
 * The enter trigger spans every Radix open-state name — `open` (Dialog, Sheet,
 * dropdown, select) **and** Tooltip's `delayed-open`/`instant-open` — so one
 * const drives all eight surfaces. The fade/zoom/slide utilities only set the
 * `--tw-enter-*`/`--tw-exit-*` keyframe vars, so they stay unprefixed; only
 * `animate-in`/`animate-out` are state-gated, keeping enter and exit mutually
 * exclusive (no two `animation` shorthands ever fight).
 *
 * Every class below is a plain string literal so Tailwind's static scanner
 * generates the utilities; do not build these from arrays/interpolation.
 *
 * Reduced motion is honoured by the blanket `prefers-reduced-motion` gate the
 * `animate-*` utilities already sit behind — no per-call guard needed.
 */

/** Fire `animate-in` on every Radix open-state name (Tooltip diverges from `open`). */
const enterTrigger =
	'data-[state=open]:animate-in data-[state=delayed-open]:animate-in data-[state=instant-open]:animate-in'

/** Enter runs at `--duration-base` for every open-state name. */
const enterDuration =
	'data-[state=open]:duration-[var(--duration-base)] data-[state=delayed-open]:duration-[var(--duration-base)] data-[state=instant-open]:duration-[var(--duration-base)]'

/**
 * Asymmetric enter/exit timing shared by every overlay: enter `--duration-base`,
 * exit `--duration-fast`, both `--ease-out`. Compose onto a bespoke overlay
 * (e.g. the Sheet's edge-slide) that can't use the fade/zoom presets below.
 */
export const overlayTiming = `ease-out ${enterDuration} data-[state=closed]:duration-[var(--duration-fast)]`

/** Fade + asymmetric-timed enter/exit trigger, minus any zoom or slide. */
const overlayFade = `${enterTrigger} data-[state=closed]:animate-out fade-in-0 fade-out-0 ${overlayTiming}`

/**
 * Fade-only scrim for a modal overlay's backdrop (Dialog / Sheet). No zoom or
 * slide — the scrim just cross-fades on the shared asymmetric timing.
 */
export const overlayScrim = overlayFade

/**
 * Centered overlay content (Dialog): fades + zooms from its own center. The
 * fade recipe plus the zoom pair.
 */
export const overlayMotion = `${overlayFade} zoom-in-95 zoom-out-95`

/**
 * Anchored overlay content (dropdown, select, tooltip, generic Popover): the
 * centered recipe re-origined onto the Radix popper transform-origin so it grows
 * from the trigger, plus a 2px directional slide toward the anchored side.
 */
export const overlayMotionAnchored = `${overlayMotion} origin-[var(--radix-popper-transform-origin)] data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2`

/**
 * Press feedback for **discrete** pressables only (Button incl. icon buttons,
 * ToggleChip, pager cells): a subtle tactile scale-down on `:active`. Gated to
 * `motion-safe` so reduced-motion users get no transform; the smoothing itself
 * rides the shared `focus-cosy` transition (which now includes `transform`), so
 * this const carries only the state, not a duplicate `transition-*`. Full-width
 * surfaces (menu/select items, rows, links, cards) get the ramp's color step
 * instead, never this.
 */
export const pressScale = 'motion-safe:active:scale-[0.97]'

/**
 * Reactive colour shift for hover / focus / selection acknowledgement on
 * full-width surfaces (menu items, nav items, rows, chips). Names its timing
 * from the motion namespace — `--duration-base` on the shared `--ease-out`
 * curve — so nothing rides Tailwind's silent 150ms default (EPT-282, audit §B).
 */
export const colorTransition =
	'transition-colors duration-[var(--duration-base)] ease-out'
